import * as React from "react";
import {Redirect} from "react-router-dom";

import {cs}             from "@Common/react/chain-services";
import {consumeContext} from "@Common/react/context";
import {oMapToArr}      from "@Common/utils/objects";

import {
    AGENT_LEVEL, BRANCH_LEVEL, COMPANY_LEVEL
} from "../common/logic/user-level";

import {renderRoutes}                        from "../routes";
import {AccountRoutesObject}                 from "./account-routes-object";
import {MailingAssetsRoute}                  from "./address-book/mailing-assets-route/mailing-assets-route";
import {MailingRoute}                        from "./address-book/mailing-route/mailing-route";
import {ShippingAddressesRoute}              from "./address-book/shipping-addresses-route/shipping-addresses-route";
import {AccountAdminAdBudgetDefaultsRoute}   from "./admin/accounting/ad-budget-defaults/ad-budget-defaults-route";
import {AccountAdminAdBudgetsRoute}          from "./admin/accounting/admin-ad-budgets-route/admin-ad-budgets-route";
import {AccountAdminVouchersRoute}           from "./admin/accounting/admin-vouchers-route/admin-vouchers-route";
import {AccountAdminListingsFormRoute}       from "./admin/listings/form/form-route";
import {AccountAdminListingsRoute}           from "./admin/listings/listings-route";
import {AccountOfficeManagementRoute}        from "./admin/office-management/office-management-route/office-management-route";
import {OfficeAdminLayoutLoader}             from "./admin/office/common/office-admin-layout-loader";
import {OfficeAddressListsRoute}             from "./admin/office/office-address-lists/office-address-lists-route";
import {OfficeMailingAssetsRoute}            from "./admin/office/office-mailing-assets/office-mailing-assets-route";
import {AccountActivityDashboardRoute}       from "./admin/reports/activity-dashboard-route/activity-dashboard";
import {AccountTransactionRoute}             from "./admin/reports/transaction-route/transaction-route";
import {AccountAdminAddressListsRoute}       from "./admin/user-management/user-assets/admin-address-lists-route/admin-address-lists-route";
import {AccountAdminCreditCardsRoute}        from "./admin/user-management/user-assets/admin-credit-cards-route/admin-credit-cards-route";
import {AccountAdminHeadshotsRoute}          from "./admin/user-management/user-assets/admin-headshots-route/admin-headshots-route";
import {AccountAdminLogosRoute}              from "./admin/user-management/user-assets/admin-logos-route/admin-logos-route";
import {AccountAdminMailingAssetsRoute}      from "./admin/user-management/user-assets/admin-mailing-assets-route/admin-mailing-assets-route";
import {AccountAdminScheduledCampaignsRoute} from "./admin/user-management/user-assets/admin-scheduled-campaigns-route/admin-scheduled-campaigns-route";
import {AccountAdminShippingRoute}           from "./admin/user-management/user-assets/admin-shipping-route/admin-shipping-route";
import {AccountAdminSocialRoute}             from "./admin/user-management/user-assets/admin-social-route/admin-social-route";
import {UserAssetsLayoutLoader}              from "./admin/user-management/user-assets/common/layout/user-assets-layout-loader";
import {UserAssetsRedirectRoute}             from "./admin/user-management/user-assets/user-assets-redirect-route";
import {AccountUserManagementRoute}          from "./admin/user-management/user-management-route/user-management-route";
import {AccountCompanyAddressBookRoute}      from "./company/company-address-book-route/company-address-book-route";
import {AccountCompanyInfoRoute}             from "./company/company-info-route/company-info-route";
import {AccountMyProfileRoute}               from "./my-profile/my-profile-route";
import {AccountAddressBookRoute}             from "./office/address-book-route/address-book-route";
import {AccountOfficeInfoRoute}              from "./office/office-info-route/office-info-route";
import {AccountApprovalRoute}                from "./orders/approval-route/approval-route";
import {AccountHistoryRoute}                 from "./orders/history-route/history-route";
import {AccountPendingRoute}                 from "./orders/pending-route/pending-route";
import {AccountScheduledRoute}               from "./orders/scheduled-route/scheduled-route";
import {AccountContactRoute}                 from "./profile/contact-route/contact-route";
import {AccountHeadshotsRoute}               from "./profile/headshots-route/headshots-route";
import {AccountLogosRoute}                   from "./profile/logos-route/logos-route";
import {AccountSecurityRoute}                from "./profile/security-route/security-route";
import {AccountSocialRoute}                  from "./profile/social-route/social-route";
import {AccountSubscriptionRoute}            from "./profile/subscription-route/subscription-route";
import {ProgramDefaultsComponents}           from "./program-defaults/program-defaults-routes";
import {AccountAdBudgetRoute}                from "./wallet/ad-budget-route/ad-budget-route";
import {WalletLayout}                        from "./wallet/common/wallet-layout";
import {AccountCreditCardsRoute}             from "./wallet/credit-cards-route/credit-cards-route";
import {AccountVouchersRoute}                from "./wallet/vouchers-route/vouchers-route";
import {AnalyticsRoute}                      from "./analytics/analytics-route";

const AccountRerouter = () => cs(
    consumeContext("accountMenu"),
    ({accountMenu}) => accountMenu && oMapToArr(accountMenu).length && (
        <Redirect to={oMapToArr(accountMenu).find(m => m.linkIndex === 0).link} />
    )
);

const PasswordRerouter = () => cs(
    consumeContext("userLevels"),
    ({userLevels}) => userLevels?.isGeneric === false && userLevels.userLevel !== AGENT_LEVEL ? (
        <Redirect to="/account/my-profile?modal=password" />
    ) : userLevels.userLevel === COMPANY_LEVEL ? (
        <Redirect to="/account/company/company-info?modal=password" />
    ) : userLevels.userLevel === BRANCH_LEVEL ? (
        <Redirect to="/account/office/office-info?modal=password" />
    ) : (
        <Redirect to="/account/profile/security?modal=password" />
    )
);

const AccountAddressBookRerouter = () => cs(
    consumeContext("userLevels"),
    ({userLevels}) => userLevels.userLevel === COMPANY_LEVEL ? (
        <Redirect to="/account/company/address-book" />
    ) : userLevels.userLevel === BRANCH_LEVEL ? (
        <Redirect to="/account/office/address-book" />
    ) : (
        <Redirect to="/account/address-book/address-lists" />
    )
)

const AccountOrdersRerouter = () => cs(
    consumeContext("accountMenu"),
    ({accountMenu}) => accountMenu?.ordersMenu?.subMenu ? (
        <Redirect to={accountMenu?.ordersMenu?.subMenu[0].link} />
    ) : accountMenu?.branchOrdersMenu?.subMenu ? (
        <Redirect to={accountMenu?.branchOrdersMenu?.subMenu[0].link} />
    ) : accountMenu?.companyOrdersMenu?.subMenu ? (
        <Redirect to={accountMenu?.companyOrdersMenu?.subMenu[0].link} />
    ) : null
);

const AccountingRerouter = () => cs (
    consumeContext("accountMenu"),
    ({accountMenu}) => accountMenu?.adminDropdownMenu?.dropdownItems?.[2]?.subMenu ? (
        <Redirect to={accountMenu?.adminDropdownMenu?.dropdownItems?.find(m => m.configPath === 'accountingMenu')?.subMenu?.[0]?.link} />
    ) : null
)

const AccountComponents: React.ComponentType<any>[] = [
    AccountActivityDashboardRoute,
    AccountAdBudgetRoute,
    AccountAddressBookRoute,
    AccountAddressBookRerouter,
    AccountAdminAddressListsRoute,
    AccountAdminAdBudgetDefaultsRoute,
    AccountAdminAdBudgetsRoute,
    AccountAdminCreditCardsRoute,
    AccountAdminHeadshotsRoute,
    AccountAdminListingsFormRoute,
    AccountAdminListingsRoute,
    AccountAdminLogosRoute,
    AccountAdminMailingAssetsRoute,
    AccountAdminScheduledCampaignsRoute,
    AccountAdminShippingRoute,
    AccountAdminSocialRoute,
    AccountAdminVouchersRoute,
    AccountApprovalRoute,
    AccountCompanyAddressBookRoute,
    AccountCompanyInfoRoute,
    AccountContactRoute,
    AccountCreditCardsRoute,
    AccountHeadshotsRoute,
    AccountHistoryRoute,
    AccountLogosRoute,
    AccountMyProfileRoute,
    AccountOfficeInfoRoute,
    AccountOfficeManagementRoute,
    AccountOrdersRerouter,
    AccountPendingRoute,
    AccountRerouter,
    AccountSecurityRoute,
    AccountScheduledRoute,
    AccountSocialRoute,
    AccountSubscriptionRoute,
    AccountTransactionRoute,
    AccountUserManagementRoute,
    AccountVouchersRoute,
    AccountingRerouter,
    AnalyticsRoute,
    MailingAssetsRoute,
    MailingRoute,
    OfficeAddressListsRoute,
    OfficeMailingAssetsRoute,
    PasswordRerouter,
    ShippingAddressesRoute,
    UserAssetsRedirectRoute,
    WalletLayout,
    ...ProgramDefaultsComponents
];

export const AccountRoutes = ({next}) => (
    renderRoutes({
        components: AccountComponents,
        contextComponents: [UserAssetsLayoutLoader, OfficeAdminLayoutLoader],
        routes: AccountRoutesObject,
        defaultRoute: "/account",
        blockGuest: true,
        next,
    })
);
